/**
 * DataSourcesPage Component (TASK-017)
 *
 * Main page for data source management.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Plus, Database, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { PageLayout } from '@/components/layout/PageLayout';
import apiClient from '@/services/api/client';
import { dataSourcesApi } from '@/services/api/dataSources';
import { indexingRunApi } from '@/services/api/indexingRun';
import { settingsApi } from '@/services/api/settings';
import { Button } from '@/components/ui/button';
import { DataSourceList } from '@/components/data-sources/DataSourceList';
import { CreateDataSourceDialog } from '@/components/data-sources/CreateDataSourceDialog';
import { IndexingPipelineView } from '@/components/data-sources/IndexingPipelineView';
import { IndexingSourceCards } from '@/components/data-sources/IndexingSourceCards';
import { IndexingStageSheet } from '@/components/data-sources/IndexingStageSheet';
import { cn } from '@/lib/utils';
import type { IndexingRun } from '@/types/indexingRun';

interface CurrentUser {
  id: string;
  permissions?: {
    data_sources?: {
      view?: boolean;
      create?: boolean;
      update?: boolean;
      delete?: boolean;
    };
  };
}
import {
  hasProcessingSource,
  hasPendingSource,
  countPendingSources,
  DATA_SOURCES_POLL_INTERVAL_MS,
} from './dataSourcesPolling';
import { Alert, AlertDescription } from '@/components/ui/alert';

/**
 * Grace window (ms) after clicking "Переиндексировать" during which we keep
 * polling even though no source has flipped to PROCESSING yet. The backend
 * marks sources PROCESSING asynchronously in a Celery task (after the HTTP
 * response returns), so an immediate refetch can still see COMPLETED. We poll
 * through this window to catch the COMPLETED → PROCESSING → COMPLETED arc.
 */
const REINDEX_AWAIT_WINDOW_MS = 20000;

export function DataSourcesPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'sources' | 'pipeline'>('sources');
  // Selected pipeline stage → drives the lazy stage-artifact Sheet.
  const [selectedOrder, setSelectedOrder] = useState<number | null>(null);
  // TASK-264: grace window right after a reindex click. The backend flips
  // sources to PROCESSING asynchronously, so we keep polling for a short while
  // to catch that transition even before any source reports PROCESSING.
  const [awaitingReindex, setAwaitingReindex] = useState(false);
  const reindexTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // TASK-267: "settle window" right after the run finishes. The backend flips
  // DataSource.status PROCESSING → COMPLETED slightly AFTER the IndexingRun
  // reports success/failed, so the list polling can stop just before catching
  // that flip (badge stays "Обработка" until F5). When the run transitions
  // running → success|failed we keep re-reading the list for a few seconds to
  // catch the late flip without a manual refresh.
  const [settling, setSettling] = useState(false);
  // Previous run status, used to detect the running → finished transition.
  const prevRunStatusRef = useRef<string | undefined>(undefined);
  // Timers scheduled during a settle burst — cleared on unmount and before a
  // new transition starts so they never pile up.
  const settleTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Set page title
  useEffect(() => {
    document.title = 'Источники данных - SMETA AI';
  }, []);

  // Clear any pending grace-window timer on unmount.
  useEffect(() => {
    return () => {
      if (reindexTimeoutRef.current) clearTimeout(reindexTimeoutRef.current);
    };
  }, []);

  // Query to get count. TASK-230: while any source is still processing
  // (PENDING/PROCESSING) poll the list so the UI updates live without F5.
  // TASK-264: also poll during the post-click grace window (awaitingReindex)
  // so the list catches the COMPLETED → PROCESSING flip the moment it lands.
  // In TanStack Query v5 the refetchInterval callback receives the Query object.
  const { data } = useQuery({
    queryKey: ['dataSources'],
    queryFn: () => dataSourcesApi.list(),
    // Keep polling alive while ANY signal is active: a grace window after a
    // reindex click, a source still PROCESSING, the run itself still running
    // (TASK-266), or the post-finish settle window (TASK-267). The run status
    // is read from the query cache (not a forward-referenced const) because
    // TanStack Query v5 evaluates this callback during observer setup, before
    // the later `run` binding is initialized. `settling`/`awaitingReindex` are
    // captured from the component closure (re-created each render, always fresh).
    refetchInterval: (query) => {
      const cachedRun = queryClient.getQueryData<IndexingRun>(['indexingRun']);
      const runRunning = cachedRun?.status === 'running';
      return awaitingReindex ||
        hasProcessingSource(query.state.data?.results) ||
        runRunning ||
        settling
        ? DATA_SOURCES_POLL_INTERVAL_MS
        : false;
    },
  });

  // TASK-270: cabinet mode drives which source types can be added. In
  // 'ecommerce' the create dialog shows only the FEED tab. retry:false keeps
  // the page resilient if the settings endpoint is unavailable.
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.getSettings,
    retry: false,
  });
  const orgMode = settings?.organization?.mode;

  // Fetch current user permissions (shared cache key with Sidebar).
  const { data: currentUser } = useQuery<CurrentUser>({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const res = await apiClient.get<CurrentUser>('/api/v1/auth/me/');
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });
  const canCreate = currentUser?.permissions?.data_sources?.create === true;
  const canUpdate = currentUser?.permissions?.data_sources?.update === true;

  // Indexing is org-wide (one IndexingRun per organization, shared by ALL
  // sources), so the pipeline visualizer lives on this all-sources panel.
  const isRunning = hasProcessingSource(data?.results);

  // Once a real PROCESSING source is observed, the grace window is no longer
  // needed — ongoing polling is driven by isRunning from here on.
  useEffect(() => {
    if (awaitingReindex && isRunning) {
      setAwaitingReindex(false);
      if (reindexTimeoutRef.current) {
        clearTimeout(reindexTimeoutRef.current);
        reindexTimeoutRef.current = null;
      }
    }
  }, [awaitingReindex, isRunning]);

  // Latest org indexing run. Poll while any source is processing, the run
  // itself reports status==='running' (live mid-flight), or during the
  // post-click grace window — so the live pipeline schema advances without F5.
  // TASK-266: the run-status check keeps polling alive even if the source list
  // briefly looks idle while the run is still executing its stages.
  const { data: run, error: runError } = useQuery({
    queryKey: ['indexingRun'],
    queryFn: indexingRunApi.getLatest,
    retry: false,
    refetchInterval: (query) =>
      isRunning ||
      awaitingReindex ||
      query.state.data?.status === 'running'
        ? DATA_SOURCES_POLL_INTERVAL_MS
        : false,
  });

  // A 404 from getLatest means "no run yet" → render the placeholder (run=null),
  // not a crash. Any other error also falls back to the empty placeholder.
  const httpStatus = (runError as { response?: { status?: number } } | null)
    ?.response?.status;
  const runOrNull = runError && httpStatus === 404 ? null : run ?? null;

  // TASK-267: detect the run finishing (running → success|failed) and kick a
  // short "settle burst" that re-reads the source list. This catches the late
  // DataSource PROCESSING → COMPLETED flip the backend performs just after the
  // run ends, so the status badge updates without F5.
  useEffect(() => {
    const prev = prevRunStatusRef.current;
    const curr = run?.status;
    prevRunStatusRef.current = curr;

    const justFinished =
      prev === 'running' && (curr === 'success' || curr === 'failed');
    if (!justFinished) return;

    // Clear any timers from a previous settle burst before scheduling new ones.
    settleTimeoutsRef.current.forEach(clearTimeout);
    settleTimeoutsRef.current = [];

    // Open the settle window so polling stays alive for its duration.
    setSettling(true);

    // Re-read the list immediately, then again at 3s / 6s / 9s. The final
    // tick closes the settle window.
    queryClient.invalidateQueries({ queryKey: ['dataSources'] });
    [3000, 6000, 9000].forEach((delay) => {
      const id = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['dataSources'] });
        if (delay === 9000) setSettling(false);
      }, delay);
      settleTimeoutsRef.current.push(id);
    });
  }, [run?.status, queryClient]);

  // Clear any pending settle timers on unmount.
  useEffect(() => {
    return () => {
      settleTimeoutsRef.current.forEach(clearTimeout);
      settleTimeoutsRef.current = [];
    };
  }, []);

  // Lazy-load the heavy per-stage artifact only when a stage is selected.
  const { data: stageDetail, isFetching: stageLoading } = useQuery({
    queryKey: ['indexingRunStage', selectedOrder],
    queryFn: () => indexingRunApi.getStage(selectedOrder as number),
    enabled: selectedOrder !== null,
  });

  const reindexAllMutation = useMutation({
    mutationFn: () => dataSourcesApi.reindexAll(),
    onSuccess: () => {
      // A reindex overwrites the single org IndexingRun → drop the cached run
      // schema and any per-stage artifact so the visualizer re-reads fresh data.
      queryClient.invalidateQueries({ queryKey: ['indexingRun'] });
      queryClient.invalidateQueries({ queryKey: ['indexingRunStage'] });
      // TASK-264: refetch the source list immediately and open the grace
      // window so polling stays on until the async PROCESSING flip is caught
      // (or the window expires as a safety net).
      queryClient.invalidateQueries({ queryKey: ['dataSources'] });
      setAwaitingReindex(true);
      if (reindexTimeoutRef.current) clearTimeout(reindexTimeoutRef.current);
      reindexTimeoutRef.current = setTimeout(() => {
        setAwaitingReindex(false);
        reindexTimeoutRef.current = null;
      }, REINDEX_AWAIT_WINDOW_MS);
      toast.success('Переиндексация документов запущена');
    },
  });

  // True while a reindex is happening: the HTTP call is in flight, the grace
  // window is open, or a source is actually PROCESSING. Drives the spinner.
  const reindexing =
    reindexAllMutation.isPending || isRunning || awaitingReindex;

  // TASK-298: a source sitting in PENDING makes hasProcessingSource() — and
  // therefore isRunning / reindexing — true, even though nothing is actually
  // being indexed (uploaded sources wait in PENDING for a manual reindex,
  // TASK-273). So the banner's "is a run truly in progress?" check looks ONLY
  // at PROCESSING (plus an in-flight mutation / the post-click grace window),
  // never at PENDING. Gating on `reindexing` here would hide the banner for the
  // very state it exists to surface.
  const runInProgress =
    (data?.results ?? []).some((s) => s.status === 'PROCESSING') ||
    reindexAllMutation.isPending ||
    awaitingReindex;
  const showPendingBanner =
    hasPendingSource(data?.results) && !runInProgress;

  // TASK-267: whether the stage currently open in the Sheet is mid-flight, so
  // the Sheet can show a "Выполняется…" note instead of an empty output block.
  const selectedStageRunning =
    selectedOrder !== null &&
    runOrNull?.stages.find((s) => s.order === selectedOrder)?.status ===
      'running';

  const handleOpenDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const handleSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['dataSources'] });
    toast.success('Источник данных создан');
  }, [queryClient]);

  const handleListSuccess = useCallback((message: string) => {
    toast.success(message);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && isDialogOpen) {
      setIsDialogOpen(false);
    }
  }, [isDialogOpen]);

  return (
    <div onKeyDown={handleKeyDown}>
      <PageLayout
        data-testid="data-sources-page"
        title="Источники данных"
        subtitle={<span className="hidden sm:inline">Управление источниками данных для индексации цен</span>}
        titleIcon={
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
            <Database className="h-5 w-5 text-white" />
          </div>
        }
        titleAddon={
          data?.count !== undefined ? (
            <span
              data-testid="total-count"
              className="hidden sm:inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold"
            >
              {data.count}
            </span>
          ) : null
        }
        breadcrumbs={[
          { label: 'Главная', href: '/' },
          { label: 'Источники данных' },
        ]}
        actions={
          (canUpdate || canCreate) ? (
            <div className="flex items-center gap-2 sm:gap-4">
              {canUpdate && (
                <Button
                  data-testid="reindex-all-button"
                  variant="outline"
                  onClick={() => reindexAllMutation.mutate()}
                  disabled={reindexing}
                >
                  <RefreshCw className={cn('h-4 w-4', reindexing && 'animate-spin')} />
                  <span className="hidden sm:inline ml-2">
                    {reindexing ? 'Переиндексация…' : 'Переиндексировать документы'}
                  </span>
                </Button>
              )}
              {canCreate && (
                <Button
                  data-testid="add-source-button"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={handleOpenDialog}
                >
                  <Plus className="h-4 w-4" />
                  <span className="hidden sm:inline ml-2">Добавить источник</span>
                </Button>
              )}
            </div>
          ) : undefined
        }
      >
        {/* KPI Row */}
        <div className="flex gap-2 -mx-6 px-6 py-2 bg-white border-b border-slate-100">
          {/* Всего источников */}
          <div className="flex-1 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2">
            <div className="w-7 h-7 rounded-md bg-blue-50 flex items-center justify-center flex-shrink-0">
              <Database className="h-3.5 w-3.5 text-blue-600" />
            </div>
            <div>
              <div className="text-[17px] font-bold text-slate-900 leading-none">{data?.count ?? 0}</div>
              <div className="text-[10.5px] text-slate-500 mt-0.5">Всего источников</div>
            </div>
          </div>

          {/* Проиндексировано */}
          <div className="flex-1 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2">
            <div className="w-7 h-7 rounded-md bg-green-50 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
            </div>
            <div>
              <div className="text-[17px] font-bold text-slate-900 leading-none">
                {data?.results?.filter((s) => s.status === 'COMPLETED').length ?? 0}
              </div>
              <div className="text-[10.5px] text-slate-500 mt-0.5">Проиндексировано</div>
            </div>
          </div>

          {/* В процессе */}
          <div className="flex-1 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2">
            <div className="w-7 h-7 rounded-md bg-blue-50 flex items-center justify-center flex-shrink-0">
              <RefreshCw className={cn('h-3.5 w-3.5 text-blue-600', isRunning && 'animate-spin')} />
            </div>
            <div>
              <div className="text-[17px] font-bold text-slate-900 leading-none">
                {data?.results?.filter((s) => s.status === 'PROCESSING' || s.status === 'PENDING').length ?? 0}
              </div>
              <div className="text-[10.5px] text-slate-500 mt-0.5">В процессе</div>
            </div>
          </div>

          {/* С ошибками */}
          <div className="flex-1 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2">
            <div className="w-7 h-7 rounded-md bg-red-50 flex items-center justify-center flex-shrink-0">
              <XCircle className="h-3.5 w-3.5 text-red-600" />
            </div>
            <div>
              <div className="text-[17px] font-bold text-slate-900 leading-none">
                {data?.results?.filter((s) => s.status === 'FAILED').length ?? 0}
              </div>
              <div className="text-[10.5px] text-slate-500 mt-0.5">С ошибками</div>
            </div>
          </div>
        </div>

        {/* Underline tabs */}
        <div className="flex gap-0 bg-white border-b border-slate-200 -mx-6 px-6 mb-4">
          <button
            onClick={() => setActiveTab('sources')}
            className={cn(
              'py-3 mr-6 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'sources'
                ? 'border-blue-600 text-blue-600 font-semibold'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            )}
          >
            Список источников
          </button>
          <button
            onClick={() => setActiveTab('pipeline')}
            className={cn(
              'py-3 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'pipeline'
                ? 'border-blue-600 text-blue-600 font-semibold'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            )}
          >
            Пайплайн индексации
          </button>
        </div>

        {/* Tab content */}
        {activeTab === 'sources' && (
          <>
            {/* TASK-298: surface uploaded-but-unindexed (PENDING) sources.
                Uploaded sources sit in PENDING until a manual reindex (TASK-273);
                the watchdog skips them, so without this banner they go
                unnoticed. Hidden while a run is truly in progress (runInProgress
                — PROCESSING / mutation / grace window, NOT bare PENDING) so it
                never duplicates the progress indicator. */}
            {showPendingBanner && (
              <Alert
                data-testid="reindex-pending-banner"
                className="mb-4 flex items-center justify-between gap-4 border-amber-200 bg-amber-50 text-amber-900"
              >
                <AlertDescription className="text-amber-900">
                  <strong>Ожидает запуска:</strong>{' '}
                  {countPendingSources(data?.results)} источник(ов) добавлено, но индексация ещё не запущена.
                  Нажмите кнопку справа →
                </AlertDescription>
                <Button
                  data-testid="reindex-pending-cta"
                  variant="default"
                  className="flex-shrink-0 bg-amber-600 text-white hover:bg-amber-700 animate-pulse-glow"
                  onClick={() => reindexAllMutation.mutate()}
                  disabled={runInProgress}
                >
                  <RefreshCw
                    className={cn('h-4 w-4', runInProgress && 'animate-spin')}
                  />
                  <span className="ml-2">Запустить индексацию</span>
                </Button>
              </Alert>
            )}
            <DataSourceList onSuccess={handleListSuccess} />
          </>
        )}
        {activeTab === 'pipeline' && (
          <div>
            <IndexingPipelineView
              run={runOrNull}
              isRunning={isRunning}
              onStageClick={setSelectedOrder}
            />
            {/* Per-source breakdown of the latest run (TASK-263). */}
            <div className="mt-4">
              <IndexingSourceCards sources={runOrNull?.sources ?? []} />
            </div>
          </div>
        )}

        <IndexingStageSheet
          stage={selectedOrder !== null ? stageDetail ?? null : null}
          open={selectedOrder !== null}
          onOpenChange={(open) => {
            if (!open) setSelectedOrder(null);
          }}
          loading={selectedOrder !== null && stageLoading}
          running={selectedStageRunning}
        />
      </PageLayout>

      {/* Create Dialog */}
      <CreateDataSourceDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        onSuccess={handleSuccess}
        mode={orgMode}
      />
    </div>
  );
}

export default DataSourcesPage;
